import { useRef, useState } from "react";

import { OTHER, type Field } from "../lib/api";
import { inp, btnGhost, XIcon } from "./ui";

// Campo editável do portal. Formato de cada tipo (e da resposta) em
// carmillion_core/self_service.py — a injeção no prompt é feita lá.

const DAYS = ["Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado", "Domingo"];
type Range = [string, string];
type Hours = Record<string, Range[]>;

/** Bateu o limite (maxLength segura o excesso): contorno vermelho + aviso, sem contador. */
const Over = () => <div style={{ fontSize: 11, color: "var(--danger)" }}>Limite de caracteres excedido</div>;
const hit = (v: string, limit?: number | null) => !!limit && v.length >= limit;

function GrowingTextarea({ value, onChange, maxLength, invalid }: { value: string; onChange: (v: string) => void; maxLength?: number; invalid?: boolean }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const grow = () => { const el = ref.current; if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; } };
  return <textarea ref={ref} rows={3} maxLength={maxLength} style={{ ...inp, resize: "none", lineHeight: 1.45, overflow: "hidden", ...(invalid ? { borderColor: "var(--danger)" } : {}) }} value={value}
    onChange={(e) => { onChange(e.target.value); grow(); }} onFocus={grow} />;
}

function Chips({ f, chosen, onChange }: { f: Field; chosen: string[]; onChange: (v: string[]) => void }) {
  const opts = f.options ?? [];
  const full = !!f.max_choices && chosen.length >= f.max_choices;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {opts.map((o) => {
        const on = chosen.includes(o.id);
        return (
          <button key={o.id} type="button" disabled={!on && full} onClick={() => onChange(on ? chosen.filter((x) => x !== o.id) : [...chosen, o.id])}
            style={{ ...btnGhost, padding: "0 12px", height: 30, borderRadius: 999, fontWeight: on ? 500 : 400, background: on ? "var(--brand-soft)" : "var(--surface-card)", borderColor: on ? "var(--brand-line)" : "var(--line-strong)", color: on ? "var(--brand)" : "var(--ink-secondary)", opacity: !on && full ? 0.5 : 1 }}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// Opção "Outros" (select/multi com f.other): entra como opção de id OTHER; ao escolher, abre
// caixa de texto (other/onOther = answers["<id>:outro"]).
const withOther = (f: Field) => (f.other ? [...(f.options ?? []), { id: OTHER, label: "Outros", text: "" }] : f.options ?? []);

function OtherBox({ f, value, onChange }: { f: Field; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <GrowingTextarea value={value} maxLength={f.other_limit ?? undefined} invalid={hit(value, f.other_limit)} onChange={onChange} />
      {hit(value, f.other_limit) && <Over />}
    </div>
  );
}

export function FieldInput({ f, value, onChange, other = "", onOther }: { f: Field; value: any; onChange: (v: any) => void; other?: string; onOther?: (v: string) => void }) {
  if (f.type === "text" || f.type === "textarea") {
    const v: string = value ?? "";
    return (
      <div>
        {f.type === "textarea"
          ? <GrowingTextarea value={v} maxLength={f.limit ?? undefined} invalid={hit(v, f.limit)} onChange={onChange} />
          : <input maxLength={f.limit ?? undefined} style={{ ...inp, ...(hit(v, f.limit) ? { borderColor: "var(--danger)" } : {}) }} value={v} onChange={(e) => onChange(e.target.value)} />}
        {hit(v, f.limit) && <Over />}
      </div>
    );
  }
  if (f.type === "slider") {
    const min = f.min ?? 0, max = f.max ?? 10, step = f.step ?? 1;
    const v = value ?? f.default ?? min;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <input type="range" min={min} max={max} step={step} value={v} onChange={(e) => onChange(Number(e.target.value))} style={{ flex: 1, accentColor: "var(--brand)" }} />
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--ink)", minWidth: 34, textAlign: "right" }}>{v}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--ink-soft)" }}>
          <span>{min}{f.min_label ? ` - ${f.min_label}` : ""}</span><span>{max}{f.max_label ? ` - ${f.max_label}` : ""}</span>
        </div>
      </div>
    );
  }
  if (f.type === "bool") {
    const on = !!value;
    return (
      <button type="button" onClick={() => onChange(!on)} style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 13, color: "var(--ink)" }}>
        <span style={{ width: 38, height: 22, borderRadius: 999, background: on ? "var(--brand)" : "var(--line-strong)", position: "relative", transition: "background .15s" }}>
          <span style={{ position: "absolute", top: 3, left: on ? 19 : 3, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left .15s" }} />
        </span>
        {on ? "Sim" : "Não"}
      </button>
    );
  }
  if (f.type === "faq") {
    type QA = { q: string; a: string };
    const items: QA[] = Array.isArray(value) ? value.filter((x) => x && typeof x === "object") : [];
    const full = !!f.max_items && items.length >= f.max_items;
    const setAt = (i: number, patch: Partial<QA>) => onChange(items.map((x, k) => (k === i ? { ...x, ...patch } : x)));
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {items.map((it, i) => (
          <div key={i} style={{ border: "1px solid var(--line)", borderRadius: 10, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input style={{ ...inp, fontWeight: 600, ...(hit(it.q ?? "", f.q_limit ?? 300) ? { borderColor: "var(--danger)" } : {}) }} maxLength={f.q_limit ?? 300} placeholder="Pergunta" value={it.q ?? ""} onChange={(e) => setAt(i, { q: e.target.value })} />
              <button type="button" title="Remover" onClick={() => onChange(items.filter((_, k) => k !== i))} style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer" }}><XIcon size={12} /></button>
            </div>
            {hit(it.q ?? "", f.q_limit ?? 300) && <Over />}
            <GrowingTextarea value={it.a ?? ""} maxLength={f.a_limit ?? undefined} invalid={hit(it.a ?? "", f.a_limit)} onChange={(v) => setAt(i, { a: v })} />
            {hit(it.a ?? "", f.a_limit) && <Over />}
          </div>
        ))}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button type="button" disabled={full} onClick={() => onChange([...items, { q: "", a: "" }])} style={{ ...btnGhost, padding: "6px 12px", opacity: full ? 0.5 : 1 }}>+ Pergunta</button>
          {f.max_items ? <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>até {f.max_items} pergunta(s)</span> : null}
        </div>
      </div>
    );
  }
  if (f.type === "list") {
    const items: string[] = Array.isArray(value) ? value : [];
    const full = !!f.max_items && items.length >= f.max_items;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {items.map((it, i) => (
          <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input style={inp} value={it} onChange={(e) => onChange(items.map((x, k) => (k === i ? e.target.value : x)))} />
            <button type="button" title="Remover" onClick={() => onChange(items.filter((_, k) => k !== i))} style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer" }}><XIcon size={12} /></button>
          </div>
        ))}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button type="button" disabled={full} onClick={() => onChange([...items, ""])} style={{ ...btnGhost, padding: "6px 12px", opacity: full ? 0.5 : 1 }}>+ Item</button>
          {f.max_items ? <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>até {f.max_items} item(ns)</span> : null}
        </div>
      </div>
    );
  }
  if (f.type === "hours") return <HoursInput value={value} onChange={onChange} />;
  const opts = withOther(f);
  if (f.type === "select") {
    const otherBox = f.other && value === OTHER && onOther ? <OtherBox f={f} value={other} onChange={onOther} /> : null;
    if (f.display === "dropdown") {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <select style={inp} value={value ?? ""} onChange={(e) => onChange(e.target.value || undefined)}>
            <option value="">— escolha —</option>
            {opts.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          {otherBox}
        </div>
      );
    }
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 8 }}>
          {opts.map((o) => {
            const on = value === o.id;
            return (
              <button key={o.id} type="button" onClick={() => onChange(on ? undefined : o.id)}
                style={{ textAlign: "left", border: `1px solid ${on ? "var(--brand)" : "var(--line-strong)"}`, boxShadow: on ? "0 0 0 1px var(--brand)" : undefined, background: on ? "var(--brand-soft)" : "var(--surface-card)", borderRadius: 9, padding: "10px 12px", cursor: "pointer", display: "flex", flexDirection: "column", gap: 3 }}>
                <b style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>{o.label}</b>
              </button>
            );
          })}
        </div>
        {otherBox}
      </div>
    );
  }
  const chosen: string[] = Array.isArray(value) ? value : [];
  const full = !!f.max_choices && chosen.length >= f.max_choices;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {f.display === "dropdown" ? (
        <>
          <select style={inp} value="" disabled={full} onChange={(e) => e.target.value && onChange([...chosen, e.target.value])}>
            <option value="">{full ? "limite atingido" : "— adicionar —"}</option>
            {opts.filter((o) => !chosen.includes(o.id)).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          {chosen.length > 0 && <Chips f={{ ...f, options: opts.filter((o) => chosen.includes(o.id)) }} chosen={chosen} onChange={onChange} />}
        </>
      ) : <Chips f={{ ...f, options: opts }} chosen={chosen} onChange={onChange} />}
      {f.other && chosen.includes(OTHER) && onOther ? <OtherBox f={f} value={other} onChange={onOther} /> : null}
      {f.max_choices ? <div style={{ fontSize: 11, color: "var(--ink-muted)" }}>até {f.max_choices} opção(ões)</div> : null}
    </div>
  );
}

/** Horário de atendimento: vem recolhido com um resumo dos dias abertos; "Editar" abre a grade por dia. */
function HoursInput({ value, onChange }: { value: any; onChange: (v: any) => void }) {
  const [open, setOpen] = useState(false);
  const h: Hours = value && typeof value === "object" ? value : {};
  const set = (d: number, rs: Range[]) => onChange({ ...h, [d]: rs });
  const summary = DAYS.map((name, d) => ({ name: name.replace("-feira", ""), rs: h[String(d)] ?? [] })).filter((x) => x.rs.length)
    .map((x) => `${x.name} ${x.rs.map((r) => `${r[0]}–${r[1]}`).join(", ")}`);
  return (
      <div style={{ border: "1px solid var(--line)", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px" }}>
          <span style={{ flex: 1, fontSize: 13, color: summary.length ? "var(--ink)" : "var(--ink-muted)", lineHeight: 1.5 }}>
            {summary.length ? summary.join(" · ") : "Nenhum dia com horário definido"}
          </span>
          <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)} style={{ ...btnGhost, height: 30, padding: "0 12px", fontSize: 12 }}>{open ? "Recolher" : "Editar"}</button>
        </div>
        {open && <div style={{ borderTop: "1px solid var(--line)" }}>
        {DAYS.map((name, d) => {
          const rs = h[String(d)] ?? [];
          const on = rs.length > 0;
          return (
            <div key={d} style={{ display: "grid", gridTemplateColumns: "150px 1fr auto", gap: 10, alignItems: "start", padding: "10px 12px", borderTop: d ? "1px solid var(--line)" : undefined }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 500, color: "var(--ink)", cursor: "pointer", height: 32 }}>
                <input type="checkbox" checked={on} onChange={(e) => set(d, e.target.checked ? [["09:00", "18:00"]] : [])} style={{ accentColor: "var(--brand)" }} />
                {name}
              </label>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {on ? rs.map((r, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input type="time" style={{ ...inp, width: 110 }} value={r[0]} onChange={(e) => set(d, rs.map((x, k) => (k === i ? [e.target.value, x[1]] : x)))} />
                    <span style={{ color: "var(--ink-muted)" }}>–</span>
                    <input type="time" style={{ ...inp, width: 110 }} value={r[1]} onChange={(e) => set(d, rs.map((x, k) => (k === i ? [x[0], e.target.value] : x)))} />
                    <button type="button" title="Remover faixa" onClick={() => set(d, rs.filter((_, k) => k !== i))} style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer" }}><XIcon size={12} /></button>
                  </div>
                )) : <span style={{ fontSize: 13, color: "var(--ink-muted)", lineHeight: "32px" }}>Fechado</span>}
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                <button type="button" title="Adicionar faixa de horário" onClick={() => set(d, [...rs, [rs.length ? rs[rs.length - 1][1] : "09:00", "18:00"]])} style={{ ...btnGhost, padding: 0, width: 30, height: 30 }}>+</button>
                {/* ponytail: copia pra todos os dias abertos; seleção de dias se pedirem */}
                <button type="button" title="Copiar este horário para os outros dias abertos" disabled={!on}
                  onClick={() => onChange(Object.fromEntries(DAYS.map((_, k) => [String(k), (h[String(k)] ?? []).length ? rs : []])))}
                  style={{ ...btnGhost, padding: 0, width: 30, height: 30, opacity: on ? 1 : 0.4 }}>⧉</button>
              </div>
            </div>
          );
        })}
        </div>}
      </div>
    );
}
